/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

theme.Product = new function () {
  const selectors = {
    productJson: "[data-product-json]",
    form: {
      container: "[data-product-form]",
      submit: "[data-add-to-cart]",
      submitText: "[data-add-to-cart-text]",
      status: "[data-add-to-cart-status]",
      message: "[data-product-form-message]"
    },
    price: {
      wrapper: "[data-product-price-wrapper]",
      regularPrice: "[data-product-price]",
      comparePrice: "[data-product-compare-price]"
    },
    sku: {
      wrapper: "[data-variant-sku-wrapper]",
      value: "[data-variant-sku]"
    },
    media: {
      zoomMedia: "zoom-media",
      slider: {
        container: "[data-product-slider]",
        slide: "[data-product-slide]",
        pagination: "[data-product-slider-pagination]",
        paginationBulletDefault: "[data-product-slider-pagination-bullet-default]",
        paginationBulletVideo: "[data-product-slider-pagination-bullet-video]"
      }
    },
    accordion: {
      content: "[data-product-accordion-content]"
    },
    widget: {
      okendoAggregateReviews: "[data-okendo-aggregate-reviews]",
      okendoAggregateReviewsText: "[data-okendo-aggregate-reviews-text]",
      attributeRating: {
        template: "[data-attribute-rating-widget]",
        value: "[data-attribute-rating-widget-value]",
        label: {
          min: "[data-attribute-rating-widget-label-min]",
          mid: "[data-attribute-rating-widget-label-mid]",
          max: "[data-attribute-rating-widget-label-max]"
        }
      }
    },
    sizeGuide: {
      popupContainer: "[data-size-guide-popup]",
      popupWrapper: "[data-size-guide-popup-wrapper]",
      popupContent: "[data-size-guide-popup-content]",
      popupContentWrapper: "[data-size-guide-popup-content-wrapper]",
      popupOpen: "[data-size-guide-open]",
      popupClose: "[data-size-guide-close]",
      tabs: "[data-size-guide-tabs]",
      aggregateRating: "[data-size-guide-aggregate-rating]",
      content: "[data-size-guide-content]"
    },
    backInStock: {
      container: "[data-back-in-stock]",
      form: "klaviyo-back-in-stock-form"
    },
    stitchedProductsJson: "[data-stitched-products-json]",
    additionalVariantsDataJson: "[data-additional-variants-data]"
  };

  const modifiers = {
    hidden: "hidden",
    initialized: "initialized",
    disabled: "disabled",
    adding: "adding",
    added: "added",
    price: {
      sale: "product-info__price-value--sale"
    },
    sizeGuide: {
      open: "size-guide-popup--open"
    },
    message: {
      error: "product-message--error",
      success: "product-message--success"
    }
  };

  const datasets = {
    productHandle: "productHandle",
    productId: "productId",
    productAgeGroup: "productAgeGroup",
    option: "option",
    optionName: "optionName",
    index: "index",
    mediaType: "mediaType",
    sizeGuidePageHandle: "sizeGuidePageHandle",
    isAvailable: "isAvailable",
    isComingSoon: "isComingSoon",
    comingSoonDate: "comingSoonDate",
    enableBackInStock: "enableBackInStock",
    enableProductStitching: "enableProductStitching",
    enableHistoryState: "enableHistoryState",
    backInStockMarketingSource: "backInStockMarketingSource",
    quantityLimit: "quantityLimit",
    quantityLimitDefault: "quantityLimitDefault",
    productType: "productType",
    groupCollection: "groupCollection"
  };

  const colorOptionLabels = 'color,colour,farbe,couleur,χρώμα,kleur,colore,cor'.split(',');

  const mediaTypes = {
    externalVideo: "external_video",
    video: "video"
  };

  const messageTypes = {
    success: "success",
    error: "error"
  };

  const okendo = {
    userId: theme.okendo_user_id,
    attributeRating: {
      types: {
        centeredRange: "centered-range"
      }
    }
  };

  class Product {
    constructor(container) {
      this.container = container;

      if (!this.container) {
        return false;
      }

      this.init();
    }

    async init() {
      const handle = this.container.dataset[datasets.productHandle];

      this.product = await this.getProductJson(handle);

      if (!this.product) {
        return false;
      }

      this.productId = this.container.dataset[datasets.productId];
      this.productAgeGroup = this.container.dataset[datasets.productAgeGroup];

      this.initElements();
      this.initProductForm();
      this.initProductMedia();
      this.initProductAccordions();

      this.isProductAvailable = (this.container.dataset[datasets.isAvailable] === "true") || false;

      this.isComingSoon = (this.container.dataset[datasets.isComingSoon] === "true") || false;
      this.comingSoonDate = this.container.dataset[datasets.comingSoonDate];

      this.quantityLimit = this.container.dataset[datasets.quantityLimit];
      this.quantityLimitDefault = this.container.dataset[datasets.quantityLimitDefault];

      this.enableProductStitching = (this.container.dataset[datasets.enableProductStitching] === "true") || false;
      this.enableBackInStock = (this.container.dataset[datasets.enableBackInStock] === "true") || false;
      this.enableHistoryState = (this.container.dataset[datasets.enableHistoryState] === "true") || false;

      this.initAdditionalVariantsData();

      this.initSizeGuide();
      this.initStitchedProducts();

      this.disableUnavailableOptions(this.currentVariant);
      this.disableUnavailableStitchedProducts(this.currentVariant);

      this.updateBackInStockForm(this.currentVariant);

      this.initQuantityLimitCheck();
    }

    async getProductJson(handle) {
      const url = `${window.Shopify.routes.root}products/${handle}.js`;

      const response = await fetch(url).then(
        (data) => data.text()
      );

      return JSON.parse(response);
    }

    async initQuantityLimitCheck() {
      this.isQuantityLimitReached = await this.checkIfQuantityLimitReached();

      document.addEventListener("cart:change", this.onQuantityLimitCheck.bind(this));
    }

    async onQuantityLimitCheck(event) {
      this.isQuantityLimitReached = await this.checkIfQuantityLimitReached(event?.detail?.cart);

      this.updateProductFormSubmit(this.currentVariant);
    }

    async checkIfQuantityLimitReached(cart) {
      const variantQuantityInCart = await this.getMatchingVariantQuantityInCart(cart);

      if (parseInt(variantQuantityInCart) === 0 && parseInt(this.quantityLimit) === 0) {
        return false;
      }

      if (parseInt(variantQuantityInCart) >= parseInt(this.quantityLimit)) {
        return true;
      }

      const productQuantityInCart = await this.getMatchingProductsQuantityInCart(cart);

      return parseInt(productQuantityInCart) >= parseInt(this.quantityLimitDefault);
    }

    async getMatchingProductsQuantityInCart(cart) {
      if (!cart || !cart.items) {
        cart = await this.getCartData();
      }

      if (!cart.items || !cart.items.length) {
        return 0;
      }

      return cart.items.reduce((totalQuantity, item) => {
        if (item.product_id === this.product.id) {
          return totalQuantity + item.quantity;
        }

        return totalQuantity;
      }, 0);
    }

    async getMatchingVariantQuantityInCart(cart) {
      if (!cart || !cart.items) {
        cart = await this.getCartData();
      }

      if (!cart.items || !cart.items.length) {
        return 0;
      }

      return cart.items.reduce((totalQuantity, item) => {
        if (item.variant_id === this.currentVariant.id) {
          return totalQuantity + item.quantity;
        }

        return totalQuantity;
      }, 0);
    }

    initElements() {
      this.productPriceWrapper = this.container.querySelector(
        selectors.price.wrapper
      );

      this.productPrice = this.container.querySelector(selectors.price.regularPrice);

      this.productComparePrice = this.container.querySelector(selectors.price.comparePrice);

      this.productFormSubmit = this.container.querySelector(
        selectors.form.submit
      );

      this.productFormSubmitText = this.container.querySelector(
        selectors.form.submitText
      );

      this.productFormStatus = this.container.querySelector(
        selectors.form.status
      );

      this.productFormMessage = this.container.querySelector(
        selectors.form.message
      );

      this.variantSkuWrappers = [...this.container.querySelectorAll(selectors.sku.wrapper)];
      this.variantSkus = [...this.container.querySelectorAll(selectors.sku.value)];

      this.backInStockContainer = this.container.querySelector(selectors.backInStock.container);
      this.backInStockForms = [...this.container.querySelectorAll(selectors.backInStock.form)];
    }

    initSlider(container, customOptions, onSlideChangeCallback = () => {}) {
      if (!container) {
        return false;
      }

      const options = {
        slidesPerView: "auto",
        spaceBetween: 0,
        navigation: false,
        pagination: false,
        on: {
          afterInit: () => {
            container.classList.add(modifiers.initialized);
          },
          slideChange: onSlideChangeCallback.bind(this)
        },
        ...customOptions
      };

      return new Swiper(container, options);
    }

    initProductForm() {
      const productFormContainer = this.container.querySelector(selectors.form.container);

      if (!productFormContainer) {
        return false;
      }

      this.productForm = new Shopify.theme.productForm.ProductForm(
        productFormContainer,
        this.product,
        {
          onOptionChange: this.onOptionChange.bind(this),
          onFormSubmit: this.onFormSubmit.bind(this),
          onQuantityChange: () => {},
          onPropertyChange: () => {}
        }
      );

      this.productOptionInputs = this.productForm.optionInputs.reduce(
        (options, input) => {
          const option = input.dataset[datasets.option];
          const inputs = options[option] || [];

          return {
            ...options,
            [option]: [...inputs, input]
          };
        },
        {}
      );

      this.currentVariant = this.productForm.variant();
    }

    initStitchedProducts() {
      this.stitchedProducts = {};

      if (!this.enableProductStitching) {
        return false;
      }

      const stitchedProductsJson = this.container.querySelector(selectors.stitchedProductsJson);

      if (!stitchedProductsJson) {
        return false;
      }

      this.stitchedProducts = JSON.parse(stitchedProductsJson.innerHTML);
    }

    initAdditionalVariantsData() {
      this.additionalVariantsData = {};

      const additionalVariantsDataJson = this.container.querySelector(selectors.additionalVariantsDataJson);

      if (!additionalVariantsDataJson) {
        return false;
      }

      this.additionalVariantsData = JSON.parse(additionalVariantsDataJson.innerHTML);
    }

    async onOptionChange(event) {
      const variant = event.dataset.variant;

      this.currentVariant = variant;

      this.updateQuantityLimit(variant);

      this.isQuantityLimitReached = await this.checkIfQuantityLimitReached();

      this.updatePrice(variant);
      this.updateProductFormSubmit(variant);
      this.updateProductFormMessage();

      this.updateOptionValues(variant);
      this.disableUnavailableOptions(variant);
      this.disableUnavailableStitchedProducts(variant);

      this.updateVariantSku(variant);

      this.updateBackInStockForm(variant);

      this.updateBrowserHistoryState(variant);
    }

    async onFormSubmit(event) {
      event.preventDefault();

      const form = event.target;
      const formData = new FormData(form);

      this.updateProductFormMessage();

      form.classList.add(modifiers.adding);

      await fetch(theme.routes.cart_add_url, {
        method: "POST",
        body: formData
      }).then((response) =>
        response.json()
      ).then((data) => {
        setTimeout(() => {
          form.classList.remove(modifiers.adding);
        }, 500);

        const { status, message } = data;

        if (status === 422) {
          return this.formSubmitErrorCallback(message);
        }

        return this.formSubmitSuccessCallback(data);
      }).catch((error) => {
        form.classList.remove(modifiers.adding);

        return this.formSubmitErrorCallback(error);
      });
    }

    formSubmitSuccessCallback(cart) {
      this.productFormSubmit.classList.add(modifiers.added);

      this.setLiveRegionText(theme.strings.product.added_to_cart);

      if (this.addedStatusTimeout) {
        clearTimeout(this.addedStatusTimeout);
      }

      setTimeout(() => {
        this.productFormSubmit.classList.remove(modifiers.added);
      }, 1500);

      this.addedStatusTimeout = setTimeout(() => {
        this.clearLiveRegionText();
      }, 5000);

      document.dispatchEvent(
        new CustomEvent("cart:change", {
          detail: {
            cart
          }
        })
      );

      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent("cart-drawer:open")
        );
      }, 500);
    }

    formSubmitErrorCallback(error) {
      this.updateProductFormMessage(messageTypes.error, error);
    }

    updateProductFormMessage(type = null, message = "") {
      if (!this.productFormMessage) {
        return false;
      }

      // Force text mutation so identical consecutive states re-announce
      this.productFormMessage.innerHTML = "";
      requestAnimationFrame(() => {
        this.productFormMessage.innerHTML = message;
      });

      switch (type) {
        case messageTypes.success: {
          this.productFormMessage.classList.remove(modifiers.message.error);
          this.productFormMessage.classList.add(modifiers.message.success);

          break;
        }

        case messageTypes.error: {
          this.productFormMessage.classList.remove(modifiers.message.success);
          this.productFormMessage.classList.add(modifiers.message.error);

          break;
        }

        default: {
          this.productFormMessage.classList.add(modifiers.hidden);
        }
      }

      this.productFormMessage.classList.toggle(modifiers.hidden, !!!message);
    }

    setLiveRegionText(text) {
      if (!this.productFormStatus) {
        return false;
      }

      // Force text mutation so identical consecutive announcements still fire
      this.productFormStatus.innerText = "";
      requestAnimationFrame(() => {
        this.productFormStatus.innerText = text;
      });
    }

    clearLiveRegionText() {
      if (!this.productFormStatus) {
        return false;
      }

      this.productFormStatus.innerText = "";
    }

    updatePrice(variant) {
      if (!this.productPriceWrapper) {
        return false;
      }

      this.productPriceWrapper.classList.toggle(modifiers.hidden, !variant);

      if (!variant) {
        return false;
      }

      this.productPrice.innerText = theme.Shopify.formatMoney(variant.price, theme.money_format);

      if (variant.compare_at_price > variant.price) {
        this.productComparePrice.innerText = theme.Shopify.formatMoney(variant.compare_at_price, theme.money_format);

        this.productPrice.classList.add(modifiers.price.sale);
        this.productComparePrice.classList.remove(modifiers.hidden);
      } else {
        this.productComparePrice.innerText = "";

        this.productPrice.classList.remove(modifiers.price.sale);
        this.productComparePrice.classList.add(modifiers.hidden);
      }
    }

    updateProductFormSubmit(variant) {
      if (!this.productFormSubmit) {
        return false;
      }

      this.productFormSubmit.classList.remove(modifiers.hidden);

      if (!variant) {
        this.setButtonState(true, theme.strings.product.unavailable);
        return false;
      }

      if (this.isQuantityLimitReached) {
        this.setButtonState(true, theme.strings.product.max_quantity_reached);

        if (this.enableBackInStock && (!variant.available || !this.isProductAvailable)) {
          this.productFormSubmit.classList.add(modifiers.hidden);
        }
      } else {
        if (this.isComingSoon) {
          const text = this.comingSoonDate
            ? `${theme.strings.product.coming_soon} - ${this.comingSoonDate}`
            : theme.strings.product.coming_soon;
          this.setButtonState(true, text);
        } else if (!variant.available || !this.isProductAvailable) {
          this.setButtonState(true, theme.strings.product.sold_out);

          if (this.enableBackInStock) {
            this.productFormSubmit.classList.add(modifiers.hidden);
          }
        } else {
          this.setButtonState(false, theme.strings.product.add_to_cart);
        }
      }
    }

    setButtonState(disabled, text) {
      this.productFormSubmit.disabled = disabled;
      this.productFormSubmitText.innerText = text;

      if (!this.productFormStatus) return;

      if (disabled) {
        this.setLiveRegionText(text);
      } else {
        this.clearLiveRegionText();
      }
    }

    updateOptionValues(variant) {
      if (!variant) {
        return false;
      }

      Object.keys(this.productOptionInputs).forEach(option => {
        const value = document.querySelector(`[data-option-value='${option}']`);

        if (!value) {
          return null;
        }

        value.innerText = variant[option] || "";
      });
    }

    disableUnavailableOptions(variant) {
      if (!Object.keys(this.productOptionInputs).length) {
        return false;
      }

      if (!variant) {
        return false;
      }

      const selectedOptions = this.getSelectedOptions(variant);

      const availableOptions = this.getAvailableValues(
        this.product.variants,
        selectedOptions
      );

      const areAllVariantsUnavailable = !this.isProductAvailable ? !this.isProductAvailable : this.product.variants.every((variant) => !variant.available);

      Object.keys(this.productOptionInputs).forEach((option) => {
        const availableValues = availableOptions[option] ?? [];

        this.productOptionInputs[option].forEach((input) => {
          const optionName = input.dataset[datasets.optionName];

          if (colorOptionLabels.includes(optionName)) {
            if (!areAllVariantsUnavailable) {
              input.classList.remove(modifiers.disabled);
              return;
            }

            input.classList.add(modifiers.disabled);
            return;
          }

          if (areAllVariantsUnavailable || !availableValues.includes(input.value)) {
            input.classList.add(modifiers.disabled);
            return;
          }

          input.classList.remove(modifiers.disabled);
        });
      });
    }

    disableUnavailableStitchedProducts() {
      if (!Object.keys(this.productOptionInputs).length || !Object.keys(this.stitchedProducts).length) {
        return false;
      }

      Object.values(this.stitchedProducts).forEach((stitchedProduct) => {
        const stitchedProductId = stitchedProduct.id;

        const stitchedProductSwatch = document.querySelector(`[data-stitched-product-id='${stitchedProductId}']`);

        if (!stitchedProductSwatch) {
          return;
        }

        const stitchedProductVariants = stitchedProduct.variants;

        const isStitchedProductAvailable = stitchedProduct.is_available;

        const areAllStitchedProductVariantsUnavailable = !isStitchedProductAvailable? !isStitchedProductAvailable : stitchedProductVariants.every((variant) => !variant.available);

        if (!areAllStitchedProductVariantsUnavailable) {
          stitchedProductSwatch.classList.remove(modifiers.disabled);
          return false;
        }

        stitchedProductSwatch.classList.add(modifiers.disabled);
      });
    }

    getSelectedOptions() {
      return this.productForm.options().reduce((options, option, index) => {
        return {
          ...options,
          [`option${index + 1}`]: option.value
        };
      }, {});
    }

    getAvailableValues(variants, selected) {
      let originalSelectedKeys = Object.keys(selected);
      let maxOptionKey = `option${originalSelectedKeys.length + 1}`;
      let variantOptions = variants.map((variant) => {
        let keys = Object.keys(variant).filter((key) => {
          return key.indexOf("option") === 0 || key === "available";
        });

        return Object.assign(
          {},
          {},
          ...keys.map((key) => {
            if (key === "available") {
              return {
                [key]: variant[key]
              };
            }

            if (key > maxOptionKey) {
              return {};
            }

            const value = variant[key];

            if (value) {
              return {
                [key]: value
              };
            }

            return {};
          })
        );
      });

      Object.keys(variantOptions[0]).forEach((key) => {
        if (key === "available") {
          return;
        }

        if (selected.hasOwnProperty(key)) {
          return;
        }

        selected[key] = null;
      });

      let selectedKeys = Object.keys(selected);
      let combinations =
        selectedKeys.length > 0
          ? selectedKeys.map((excludeKey) => {
            return Object.assign(
              {},
              {},
              ...selectedKeys.map((key) => {
                if (excludeKey === key) {
                  return {};
                }

                return {
                  [key]: selected[key]
                };
              })
            );
          })
          : [{}];

      let availableValues = {};
      variantOptions
        .map((options) => {
          return Object.assign(
            {},
            {},
            ...combinations.map((combination) => {
              const isSubset =
                Object.keys(combination)
                  .map((key) => {
                    if (combination[key] === null) {
                      return true;
                    }
                    return (
                      options.hasOwnProperty(key) &&
                      combination[key] === options[key]
                    );
                  })
                  .includes(false) !== true;

              if (!isSubset) {
                return {};
              }

              if (!options.available) {
                return {};
              }

              return Object.assign(
                {},
                {},
                ...Object.keys(options)
                  .filter((key) => {
                    return (
                      key !== "available" &&
                      !combination.hasOwnProperty(key)
                    );
                  })
                  .map((optionName) => {
                    return {
                      [optionName]: [options[optionName]]
                    };
                  })
              );
            })
          );
        })
        .filter((options) => {
          return Object.keys(options).length > 0;
        })
        .forEach((options) => {
          Object.keys(options).forEach((key) => {
            if (!availableValues.hasOwnProperty(key)) {
              availableValues[key] = [];
            }
            availableValues[key] = availableValues[key]
              .concat(options[key])
              .filter((value, index, self) => {
                return self.indexOf(value) === index;
              });
          });
        });

      return Object.assign(
        {},
        {},
        ...Object.keys(availableValues)
          .sort()
          .map((key) => {
            return {
              [key]: availableValues[key]
            };
          })
      );
    }

    updateQuantityLimit(variant) {
      if (!variant || !this.additionalVariantsData.hasOwnProperty(variant.id)) {
        return false;
      }

      variant = {
        ...variant,
        ...this.additionalVariantsData[variant.id]
      };

      const inventoryPolicy = variant.inventory_policy;
      const inventoryQuantity = parseInt(variant.inventory_quantity);

      if (inventoryPolicy === "continue") {
        this.quantityLimit = this.quantityLimitDefault;

        return false;
      }

      if (inventoryQuantity < 0) {
        this.quantityLimit = this.quantityLimitDefault;

        return false;
      }

      if (inventoryQuantity <= parseInt(this.quantityLimitDefault)) {
        this.quantityLimit = inventoryQuantity;
      } else {
        this.quantityLimit = parseInt(this.quantityLimitDefault);
      }
    }

    updateVariantSku(variant) {
      if (!this.variantSkuWrappers.length) {
        return false;
      }

      this.variantSkuWrappers.forEach((element, index) => {
        if (!variant || !variant.sku) {
          this.variantSkus[index].innerText = "";
          this.variantSkuWrappers[index].classList.add(modifiers.hidden);

          return false;
        }

        this.variantSkus[index].innerText = variant.sku;
        this.variantSkuWrappers[index].classList.remove(modifiers.hidden);
      });
    }

    updateBrowserHistoryState(variant) {
      if (!variant || !this.enableHistoryState) {
        return false;
      }

      const url = Shopify.theme.productForm.getUrlWithVariant(window.location.href, variant.id);

      window.history.replaceState({ path: url }, "", url);
    }

    initProductMedia() {
      const sliderContainer = this.container.querySelector(selectors.media.slider.container);

      if (!sliderContainer) {
        return false;
      }

      const paginationBullet = {
        default: sliderContainer.querySelector(selectors.media.slider.paginationBulletDefault),
        video: sliderContainer.querySelector(selectors.media.slider.paginationBulletVideo)
      };

      const slides = [...sliderContainer.querySelectorAll(selectors.media.slider.slide)];

      const slidesTypes = slides.reduce(
        (types, slide) => {
          const type = slide.dataset[datasets.mediaType];

          return [
            ...types,
            type
          ];
        },
        []
      );

      const getBulletIconBySlideType = (slideType) => {
        if (!slideType) {
          return paginationBullet.default.innerHTML;
        }

        switch (slideType) {
          case mediaTypes.externalVideo:
            return paginationBullet.video.innerHTML;
          case mediaTypes.video:
            return paginationBullet.video.innerHTML;
          default:
            return paginationBullet.default.innerHTML;
        }
      };

      const sliderOptions = {
        pagination: {
          el: selectors.media.slider.pagination,
          clickable: true,
          renderBullet: (index, className) => {
            return `<button type="button" class="${className} product-slider__pagination-item" aria-label="Go to product media ${index + 1}">
              ${getBulletIconBySlideType(slidesTypes[index])}
            </button>`;
          }
        }
      };

      const onSlideChange = () => {
        const previousSlide = slides.find((slide) => {
          return parseInt(slide.dataset.index) === parseInt(this.productSlider.previousIndex)
        });

        if (!previousSlide) {
          return;
        }

        const zoomMedia = previousSlide.querySelector(selectors.media.zoomMedia);

        if (zoomMedia) {
          zoomMedia.zoomOut();
        }
      };

      this.productSlider = this.initSlider(sliderContainer, sliderOptions, onSlideChange);
    }

    initProductAccordions() {
      const accordionContents = [...document.querySelectorAll(selectors.accordion.content)];

      if (!accordionContents.length) {
        return false;
      }

      accordionContents.forEach(async (accordionContent) => {
        const okendoAggregateReviewsWidgetElement = accordionContent.querySelector(selectors.widget.okendoAggregateReviews);
        const okendoAggregateReviewsWidgetTextElement = accordionContent.querySelector(selectors.widget.okendoAggregateReviewsText);

        if (okendoAggregateReviewsWidgetElement) {
          const okendoAggregateReviewsWidget = await this.buildOkendoAggregateReviewsWidget();

          if (!okendoAggregateReviewsWidget && okendoAggregateReviewsWidgetTextElement) {
            okendoAggregateReviewsWidgetTextElement.hidden = true;
            return false;
          }

          okendoAggregateReviewsWidgetElement.innerHTML = okendoAggregateReviewsWidget;
          okendoAggregateReviewsWidgetElement.hidden = false;
        }
      });
    }

    async buildOkendoAggregateReviewsWidget() {
      const attributeRatingTemplate = this.container.querySelector(selectors.widget.attributeRating.template);

      if (!attributeRatingTemplate) {
        return false;
      }

      const attributeRatingTemplateContent = attributeRatingTemplate.content;

      const ratingValue = attributeRatingTemplateContent.querySelector(selectors.widget.attributeRating.value);

      const ratingLabelMin = attributeRatingTemplateContent.querySelector(selectors.widget.attributeRating.label.min);
      const ratingLabelMid = attributeRatingTemplateContent.querySelector(selectors.widget.attributeRating.label.mid);
      const ratingLabelMax = attributeRatingTemplateContent.querySelector(selectors.widget.attributeRating.label.max);

      const url = `https://api.okendo.io/v1/stores/${okendo.userId}/products/shopify-${this.product.id}/review_aggregate`;

      const data = await fetch(url)
        .then((response) => response.json())
        .then((data) => data);

      const { reviewAggregate } = data;

      if (!reviewAggregate) {
        return false;
      }

      const { attributeRatings } = reviewAggregate;

      if (!attributeRatings?.length) {
        return false;
      }

      const [attributeRating] = attributeRatings;

      const { type, value, minLabel, midLabel, maxLabel } = attributeRating;

      if (type !== okendo.attributeRating.types.centeredRange) {
        return false;
      }

      ratingLabelMin.innerText = minLabel;
      ratingLabelMid.innerText = midLabel;
      ratingLabelMax.innerText = maxLabel;

      ratingValue.style.left = `calc(50% + ${(value / 2 * 100)}%)`;

      return attributeRatingTemplate.innerHTML;
    }

    updateBackInStockForm(variant) {
      if (!this.backInStockContainer) {
        return false;
      }

      if (!variant) {
        this.backInStockContainer.classList.add(modifiers.hidden);
        return false;
      }

      if (!variant.available || !this.isProductAvailable) {
        this.backInStockContainer.classList.remove(modifiers.hidden);
      } else {
        this.backInStockContainer.classList.add(modifiers.hidden);
      }

      this.backInStockForms.forEach((form) => {
        form.setAttribute("variant-id", variant.id);
      });
    }

    async initSizeGuide() {
      this.sizeGuidePopup = document.querySelector(selectors.sizeGuide.popupContainer);

      if (!this.sizeGuidePopup) {
        return false;
      }

      const pageHandle = this.sizeGuidePopup.dataset[datasets.sizeGuidePageHandle];

      const sizeGuidePageContent = await this.getSizeGuidePopupContent(pageHandle);

      if (!sizeGuidePageContent) {
        return false;
      }

      const sizeGuidePageContentParsed = new DOMParser().parseFromString(sizeGuidePageContent, "text/html");

      if (!sizeGuidePageContentParsed) {
        return false;
      }

      const sizeGuideContent = sizeGuidePageContentParsed.querySelector(selectors.sizeGuide.content)?.innerHTML;

      if (!sizeGuideContent) {
        return false;
      }

      const popupWrapper = this.sizeGuidePopup.querySelector(selectors.sizeGuide.popupWrapper);
      const popupContentWrapper = this.sizeGuidePopup.querySelector(selectors.sizeGuide.popupContentWrapper);

      popupContentWrapper.innerHTML = sizeGuideContent;

      this.initSizeGuideTabs();
      this.initSizeGuideAggregateRating();

      const openPopup = () => {
        bodyScrollLock.disableBodyScroll(popupWrapper);
        this.sizeGuidePopup.classList.add(modifiers.sizeGuide.open);

        const focusableElements = this.sizeGuidePopup.getFocusableElements();
        const firstFocusableElement = focusableElements[0];

        setTimeout(() => {
          firstFocusableElement.focus();
        }, 400);

        this.sizeGuidePopup.trapFocus();
      };

      const closePopup = () => {
        this.sizeGuidePopup.removeTrapFocus();

        this.sizeGuidePopup.classList.remove(modifiers.sizeGuide.open);
        bodyScrollLock.enableBodyScroll(popupWrapper);
      };

      document.addEventListener("click", (event) => {
        const target = event.target;

        const isOpenButton = target.closest(selectors.sizeGuide.popupOpen);
        const isCloseButton = target.closest(selectors.sizeGuide.popupClose);

        if (isOpenButton) {
          event.preventDefault();
          openPopup();

          return false;
        }

        if (isCloseButton) {
          event.preventDefault();
          closePopup();

          return false;
        }
      });
    }

    async getSizeGuidePopupContent(pageHandle) {
      if (!pageHandle) {
        return false;
      }

      return await fetch(`/pages/${pageHandle}`).then(
        (data) => data.text()
      );
    }

    initSizeGuideTabs() {
      this.sizeGuideTabs = this.sizeGuidePopup.querySelector(selectors.sizeGuide.tabs);

      if (!this.sizeGuideTabs) {
        return false;
      }

      if (this.productAgeGroup) {
        this.sizeGuideTabs.switchTabByHandle(this.productAgeGroup);
      }
    }

    async initSizeGuideAggregateRating() {
      const wrapperElement = this.container.querySelector(selectors.sizeGuide.aggregateRating);

      if (!wrapperElement) {
        return false;
      }

      const widgetElement = wrapperElement.querySelector(selectors.widget.okendoAggregateReviews);

      if (!widgetElement) {
        return false;
      }

      const okendoAggregateReviewsWidget = await this.buildOkendoAggregateReviewsWidget();

      if (!okendoAggregateReviewsWidget) {
        return false;
      }

      widgetElement.innerHTML = okendoAggregateReviewsWidget;
      wrapperElement.classList.remove(modifiers.hidden);
    }

    async getCartData() {
      return await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    }
  }

  this.onSectionLoad = async function (container) {
    if (!container) {
      return false;
    }

    return new Product(container);
  };
}();

theme.Sections.register("product", theme.Product);
